import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@project/ui";
import { Button } from "@project/ui";
import { formatShort } from "@project/ui";
import type { IconName } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { useActiveHouse } from "@apps/dashboard/features/houses/active-house-context";
import { useSyncHouseFromParams } from "@apps/dashboard/features/houses/use-sync-house-from-params";
import {
  fetchVaultItems,
  homeExpenseQueryKeys,
  openStorageDocumentInNewTab,
} from "@apps/dashboard/lib/home-expense-api";
import type { VaultItem } from "@apps/dashboard/lib/home-expense-types";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import {
  isVaultCategorySlug,
  normalizeVaultSectionKey,
  VAULT_CATEGORY_SLUGS,
  warrantyDaysRemaining,
  warrantyElapsedPercent,
  warrantyUrgency,
} from "@apps/dashboard/lib/vault-ui";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { LoadingState } from "@apps/dashboard/components/states/LoadingState";
import { Icon, cn } from "@project/ui";

const SECTION_ORDER = [...VAULT_CATEGORY_SLUGS, "__none__"] as const;

function sectionKey(item: VaultItem): string {
  return normalizeVaultSectionKey(item.assetType);
}

function orderedSectionKeys(keys: Iterable<string>): string[] {
  const arr = [...new Set(keys)];
  return arr.sort((a, b) => {
    const ia = SECTION_ORDER.indexOf(a as (typeof SECTION_ORDER)[number]);
    const ib = SECTION_ORDER.indexOf(b as (typeof SECTION_ORDER)[number]);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  });
}

function sectionTitle(t: (k: string, p?: Record<string, string | number>) => string, key: string): string {
  if (key === "__none__") return t("dashboardUi.vault.sectionUncategorized");
  if (isVaultCategorySlug(key)) return t(`dashboardUi.vault.categories.${key}`);
  return key;
}

function vaultSectionIcon(key: string): IconName {
  if (isVaultCategorySlug(key)) {
    switch (key) {
      case "appliances":
        return "UtensilsCrossed";
      case "electronics":
        return "Laptop";
      case "furniture":
        return "Armchair";
      case "vehicles":
        return "Car";
      case "other":
        return "Package";
      default:
        return "Package";
    }
  }
  return "LayoutGrid";
}

function pickAlertItem(items: VaultItem[]): VaultItem | null {
  const withW = items.filter((i) => i.warrantyEndsOn);
  if (withW.length === 0) return null;
  const urgent = withW
    .map((i) => ({ i, d: warrantyDaysRemaining(i.warrantyEndsOn!) }))
    .filter((x) => x.d >= 0 && x.d <= 14)
    .sort((a, b) => a.d - b.d);
  if (urgent.length) return urgent[0]!.i;
  const expired = withW
    .map((i) => ({ i, d: warrantyDaysRemaining(i.warrantyEndsOn!) }))
    .filter((x) => x.d < 0)
    .sort((a, b) => b.d - a.d);
  if (expired.length) return expired[0]!.i;
  return null;
}

function warrantyBadgeLabel(
  t: (k: string, p?: Record<string, string | number>) => string,
  warrantyEndsOn: string | null | undefined,
): string {
  if (!warrantyEndsOn) return t("dashboardUi.vault.warrantyBadgeNone");
  const d = warrantyDaysRemaining(warrantyEndsOn);
  if (d < 0) return t("dashboardUi.vault.warrantyBadgeExpired");
  if (d === 0) return t("dashboardUi.vault.warrantyEndsToday");
  if (d <= 14) return t("dashboardUi.vault.warrantyBadgeUrgent");
  if (d <= 90) return t("dashboardUi.vault.warrantyBadgeWarning");
  return t("dashboardUi.vault.warrantyBadgeSafe");
}

function badgeTone(
  warrantyEndsOn: string | null | undefined,
): "error" | "tertiary" | "secondary" | "primary" | "muted" {
  const u = warrantyUrgency(warrantyEndsOn);
  if (u === "expired") return "error";
  if (u === "urgent" || u === "warning") return "tertiary";
  if (u === "safe") return "secondary";
  return "muted";
}

function badgeClass(tone: ReturnType<typeof badgeTone>): string {
  switch (tone) {
    case "error":
      return "bg-error-container text-on-error-container";
    case "tertiary":
      return "bg-tertiary-container/15 text-tertiary";
    case "secondary":
      return "bg-secondary-container/30 text-on-secondary-container";
    case "primary":
      return "bg-primary-container/15 text-primary";
    default:
      return "bg-surface-container-high text-outline";
  }
}

function barClass(tone: ReturnType<typeof badgeTone>): string {
  switch (tone) {
    case "error":
      return "bg-error";
    case "tertiary":
      return "bg-tertiary";
    case "secondary":
      return "bg-secondary";
    case "primary":
      return "bg-primary";
    default:
      return "bg-outline-variant";
  }
}

function statusIconName(u: ReturnType<typeof warrantyUrgency>): IconName {
  switch (u) {
    case "expired":
      return "CircleAlert";
    case "urgent":
    case "warning":
      return "AlertTriangle";
    case "safe":
      return "CheckCircle2";
    default:
      return "Minus";
  }
}

function statusIconClass(u: ReturnType<typeof warrantyUrgency>): string {
  switch (u) {
    case "expired":
      return "text-error";
    case "urgent":
    case "warning":
      return "text-tertiary";
    case "safe":
      return "text-secondary";
    default:
      return "text-on-surface-variant";
  }
}

export default function VaultListPage(): JSX.Element {
  const { houseId } = useParams<{ houseId: string }>();
  const { t, locale } = useI18n();
  const { houses } = useActiveHouse();
  const actorRole = houseId ? houses?.find((h) => h.id === houseId)?.role : undefined;
  const canEdit = actorRole === "owner" || actorRole === "admin";
  const [filterTab, setFilterTab] = useState<string>("all");
  useSyncHouseFromParams(houseId);

  const vaultQuery = useQuery({
    queryKey: houseId ? homeExpenseQueryKeys.vault(locale, houseId) : ["vault", "noop"],
    queryFn: () => {
      if (!houseId) throw new Error("missing houseId");
      return fetchVaultItems(locale, houseId);
    },
    enabled: Boolean(houseId),
  });

  const items = vaultQuery.data ?? [];

  if (!houseId) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <ErrorState />
      </div>
    );
  }

  const alertItem = items.length ? pickAlertItem(items) : null;
  const securedCount = items.filter(
    (i) => i.warrantyEndsOn && warrantyDaysRemaining(i.warrantyEndsOn) >= 0,
  ).length;
  const securedPct = items.length ? Math.round((securedCount / items.length) * 100) : 0;

  const filteredItems =
    filterTab === "all"
      ? items
      : items.filter((i) => {
          const k = sectionKey(i);
          return k === filterTab;
        });

  const groups = new Map<string, VaultItem[]>();
  for (const item of filteredItems) {
    const k = sectionKey(item);
    const list = groups.get(k) ?? [];
    list.push(item);
    groups.set(k, list);
  }
  const sectionKeys = orderedSectionKeys(groups.keys());

  function renderCard(item: VaultItem): JSX.Element {
    const tone = badgeTone(item.warrantyEndsOn);
    const u = warrantyUrgency(item.warrantyEndsOn);
    const progress = warrantyElapsedPercent(item.purchaseDate, item.warrantyEndsOn);
    const detailPath = dashboardPaths.houseVaultDetail(houseId!, item.id);

    return (
      <li key={item.id}>
        <div className="group flex h-full flex-col rounded-[2rem] bg-surface-container-lowest outline outline-1 outline-transparent transition-all hover:bg-white hover:shadow-2xl hover:shadow-black/[0.03] hover:outline-outline-variant/10">
          <Link
            to={detailPath}
            className="flex flex-1 flex-col gap-6 p-6 pb-4 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary rounded-t-[2rem]"
          >
          <div className="flex items-start justify-between gap-3">
            <div
              className={`flex size-14 items-center justify-center rounded-2xl ${
                tone === "error"
                  ? "bg-error-container/15"
                  : tone === "tertiary"
                    ? "bg-tertiary-container/10"
                    : tone === "secondary"
                      ? "bg-secondary-container/15"
                      : "bg-primary/10"
              }`}
            >
              <Icon name="Shield" className="size-7 text-primary" strokeWidth={1.5} />
            </div>
            <span
              className={`text-[0.65rem] font-bold uppercase tracking-tighter ${badgeClass(tone)} rounded-full px-3 py-1`}
            >
              {warrantyBadgeLabel(t, item.warrantyEndsOn)}
            </span>
          </div>
          <div>
            <h4 className="font-headline text-lg font-bold text-on-surface">{item.name}</h4>
            <div className="mt-4 space-y-2">
              {item.purchaseDate ? (
                <div className="flex items-center justify-between text-on-surface-variant">
                  <span className="text-[0.75rem]">{t("dashboardUi.vault.cardPurchaseDate")}</span>
                  <span className="font-headline text-sm font-bold text-on-surface">
                    {formatShort(`${item.purchaseDate}T12:00:00.000Z`)}
                  </span>
                </div>
              ) : null}
              {item.warrantyEndsOn ? (
                <div className="flex items-center justify-between text-on-surface-variant">
                  <span className="text-[0.75rem]">{t("dashboardUi.vault.cardExpiryDate")}</span>
                  <span
                    className={`font-headline text-sm font-bold ${
                      tone === "error" ? "text-error" : tone === "tertiary" ? "text-tertiary" : "text-on-surface"
                    }`}
                  >
                    {formatShort(`${item.warrantyEndsOn}T12:00:00.000Z`)}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
          {item.purchaseDate && item.warrantyEndsOn ? (
            <div
              className="h-1.5 w-full overflow-hidden rounded-full bg-surface-container shadow-[inset_0_1px_3px_rgba(0,0,0,0.1)]"
              aria-hidden
            >
              <div
                className={`h-full rounded-full shadow-inner ${barClass(tone)}`}
                style={{ width: `${Math.min(100, progress)}%` }}
              />
            </div>
          ) : null}
          </Link>
          <div className="flex items-center justify-between border-t border-outline-variant/10 px-6 pb-5 pt-3">
            {item.documentStorageKey ? (
              <button
                type="button"
                className="inline-flex items-center gap-2 text-[0.75rem] font-bold uppercase tracking-wider text-primary hover:underline"
                onClick={() => {
                  void openStorageDocumentInNewTab(locale, item.documentStorageKey!).catch(() => {
                    window.alert(t("dashboardUi.states.errorHint"));
                  });
                }}
              >
                <Icon name="FileText" className="size-4" strokeWidth={1.5} />
                {t("dashboardUi.vault.viewDocument")}
              </button>
            ) : (
              <span />
            )}
            <Icon name={statusIconName(u)} className={`size-5 shrink-0 ${statusIconClass(u)}`} strokeWidth={1.5} />
          </div>
        </div>
      </li>
    );
  }

  return (
    <div className={cn(DASHBOARD_PAGE_CONTAINER, "space-y-10 md:space-y-12")}>
      <div className="flex flex-col gap-6 md:flex-row md:flex-wrap md:items-start md:justify-between">
        <div className="max-w-xl">
          <span className="mb-2 block text-[0.75rem] font-bold uppercase tracking-widest text-outline">
            {t("dashboardUi.pages.vault")}
          </span>
          <h1 className="font-headline text-[2rem] font-extrabold leading-tight tracking-tight text-primary md:text-[2.5rem]">
            {t("dashboardUi.vault.pageTitle")}
          </h1>
          <p className="mt-4 text-body-md text-on-surface-variant">{t("dashboardUi.vault.pageSubtitle")}</p>
        </div>
        {canEdit ? (
          <Button asChild className="primary-gradient shrink-0 rounded-xl px-6 text-on-primary">
            <Link to={dashboardPaths.houseVaultNew(houseId)}>
              <span className="inline-flex items-center gap-2">
                <Icon name="Plus" className="size-4" strokeWidth={1.5} />
                {t("dashboardUi.vault.addCta")}
              </span>
            </Link>
          </Button>
        ) : null}
      </div>

      {vaultQuery.isLoading ? (
        <LoadingState />
      ) : vaultQuery.isError ? (
        <ErrorState onRetry={() => void vaultQuery.refetch()} />
      ) : items.length === 0 ? (
        <div className="rounded-[2rem] bg-surface-container-low px-6 py-14 text-center outline outline-1 outline-outline-variant/15">
          <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Icon name="Lock" className="size-8" strokeWidth={1.5} />
          </div>
          <p className="text-body-md text-on-surface-variant">{t("dashboardUi.vault.emptyHint")}</p>
          {canEdit ? (
            <Button className="primary-gradient mt-6 rounded-xl text-on-primary" asChild>
              <Link to={dashboardPaths.houseVaultNew(houseId)}>{t("dashboardUi.vault.addCta")}</Link>
            </Button>
          ) : (
            <p className="mt-4 text-label-md text-on-surface-variant">{t("dashboardUi.vault.readOnlyHint")}</p>
          )}
        </div>
      ) : (
        <>
          {alertItem ? (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-8">
              <div className="relative flex flex-col justify-between overflow-hidden rounded-[2rem] bg-tertiary-container p-8 text-on-tertiary md:col-span-7">
                <div className="relative z-10">
                  <span className="font-label text-[0.75rem] font-bold uppercase tracking-widest opacity-80">
                    {t("dashboardUi.vault.alertKicker")}
                  </span>
                  <h3 className="mt-2 font-headline text-2xl font-bold md:text-3xl">{alertItem.name}</h3>
                  <p className="mt-2 max-w-md opacity-90">
                    {alertItem.warrantyEndsOn && warrantyDaysRemaining(alertItem.warrantyEndsOn) < 0
                      ? t("dashboardUi.vault.alertExpired", { name: alertItem.name })
                      : t("dashboardUi.vault.alertDaysLeft", {
                          name: alertItem.name,
                          days: warrantyDaysRemaining(alertItem.warrantyEndsOn!),
                        })}
                  </p>
                </div>
                <div className="relative z-10 mt-8">
                  <Button
                    asChild
                    className="rounded-xl bg-surface-container-lowest font-bold text-tertiary hover:bg-white"
                  >
                    <Link to={dashboardPaths.houseVaultDetail(houseId, alertItem.id)}>
                      {t("dashboardUi.vault.alertViewDetails")}
                    </Link>
                  </Button>
                </div>
                <div className="absolute -right-10 -top-10 size-64 rounded-full bg-white/10 blur-3xl" aria-hidden />
                <Icon
                  name="AlertTriangle"
                  className="pointer-events-none absolute bottom-8 right-8 size-[8rem] opacity-10 text-on-tertiary"
                  strokeWidth={1.25}
                />
              </div>
              <div className="grid grid-cols-1 gap-6 md:col-span-5">
                <div className="flex items-center justify-between rounded-[1.5rem] bg-surface-container-low p-6">
                  <div>
                    <span className="font-label text-[0.7rem] font-bold uppercase tracking-widest text-outline">
                      {t("dashboardUi.vault.statTotalLabel")}
                    </span>
                    <div className="font-headline text-3xl font-bold text-primary">{items.length}</div>
                  </div>
                  <div className="flex size-12 items-center justify-center rounded-2xl bg-primary/5 text-primary">
                    <Icon name="Package" className="size-6" strokeWidth={1.5} />
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-[1.5rem] bg-secondary-container/20 p-6">
                  <div className="min-w-0 flex-1 pr-3">
                    <span className="font-label text-[0.7rem] font-bold uppercase tracking-widest text-secondary">
                      {t("dashboardUi.vault.statSecuredLabel")}
                    </span>
                    <div className="font-headline text-3xl font-bold text-secondary">{securedPct}%</div>
                    <p className="mt-2 text-[0.7rem] text-on-surface-variant">
                      {t("dashboardUi.vault.statSecuredLine")}
                    </p>
                  </div>
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-secondary/5 text-secondary">
                    <Icon name="BadgeCheck" className="size-6" strokeWidth={1.5} />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
              <div className="flex items-center justify-between rounded-[1.5rem] bg-surface-container-low p-6 md:col-span-6">
                <div>
                  <span className="font-label text-[0.7rem] font-bold uppercase tracking-widest text-outline">
                    {t("dashboardUi.vault.statTotalLabel")}
                  </span>
                  <div className="font-headline text-3xl font-bold text-primary">{items.length}</div>
                </div>
                <div className="flex size-12 items-center justify-center rounded-2xl bg-primary/5 text-primary">
                  <Icon name="Package" className="size-6" strokeWidth={1.5} />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-[1.5rem] bg-secondary-container/20 p-6 md:col-span-6">
                <div className="min-w-0 flex-1 pr-3">
                  <span className="font-label text-[0.7rem] font-bold uppercase tracking-widest text-secondary">
                    {t("dashboardUi.vault.statSecuredLabel")}
                  </span>
                  <div className="font-headline text-3xl font-bold text-secondary">{securedPct}%</div>
                  <p className="mt-2 text-[0.7rem] text-on-surface-variant">
                    {t("dashboardUi.vault.statSecuredLine")}
                  </p>
                </div>
                <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-secondary/5 text-secondary">
                  <Icon name="BadgeCheck" className="size-6" strokeWidth={1.5} />
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-6 overflow-x-auto pb-2 md:gap-8 md:pb-4">
            <button
              type="button"
              onClick={() => setFilterTab("all")}
              className={`shrink-0 whitespace-nowrap pb-2 font-label text-[0.75rem] font-bold uppercase tracking-widest transition-colors ${
                filterTab === "all"
                  ? "border-b-2 border-primary text-primary"
                  : "text-outline hover:text-primary"
              }`}
            >
              {t("dashboardUi.vault.filterAll")}
            </button>
            {VAULT_CATEGORY_SLUGS.map((slug) => (
              <button
                key={slug}
                type="button"
                onClick={() => setFilterTab(slug)}
                className={`shrink-0 whitespace-nowrap pb-2 font-label text-[0.75rem] font-bold uppercase tracking-widest transition-colors ${
                  filterTab === slug
                    ? "border-b-2 border-primary text-primary"
                    : "text-outline hover:text-primary"
                }`}
              >
                {t(`dashboardUi.vault.categories.${slug}`)}
              </button>
            ))}
          </div>

          {filteredItems.length === 0 ? (
            <p className="text-body-md text-on-surface-variant">
              {filterTab === "all" ? t("dashboardUi.vault.emptyHint") : t("dashboardUi.vault.filterEmpty")}
            </p>
          ) : filterTab === "all" ? (
            <div className="space-y-16">
              {sectionKeys.map((key) => {
                const sectionItems = groups.get(key) ?? [];
                if (sectionItems.length === 0) return null;
                return (
                  <section key={key}>
                    <div className="mb-8 flex items-center justify-between gap-4">
                      <h3 className="flex items-center gap-3 font-headline text-xl font-bold text-primary">
                        <Icon name={vaultSectionIcon(key)} className="size-6 text-primary" strokeWidth={1.5} />
                        {sectionTitle(t, key)}
                      </h3>
                      <span className="rounded-full bg-surface-container-high px-3 py-1 text-[0.7rem] font-bold uppercase tracking-tight text-outline">
                        {t("dashboardUi.vault.sectionCount", { count: sectionItems.length })}
                      </span>
                    </div>
                    <ul className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                      {sectionItems.map((item) => renderCard(item))}
                    </ul>
                  </section>
                );
              })}
              {canEdit ? (
                <Link
                  to={dashboardPaths.houseVaultNew(houseId)}
                  className="flex min-h-[10rem] flex-col items-center justify-center gap-4 rounded-[2rem] border-2 border-dashed border-outline-variant p-8 text-outline transition-colors hover:border-primary hover:text-primary"
                >
                  <div className="flex size-12 items-center justify-center rounded-full border-2 border-dashed border-inherit">
                    <Icon name="Plus" className="size-6" strokeWidth={1.5} />
                  </div>
                  <span className="font-label text-[0.75rem] font-bold uppercase tracking-widest">
                    {t("dashboardUi.vault.registerNewItem")}
                  </span>
                </Link>
              ) : null}
            </div>
          ) : (
            <ul className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredItems.map((item) => renderCard(item))}
              {canEdit ? (
                <li>
                  <Link
                    to={dashboardPaths.houseVaultNew(houseId)}
                    className="flex h-full min-h-[14rem] flex-col items-center justify-center gap-4 rounded-[2rem] border-2 border-dashed border-outline-variant p-6 text-outline transition-colors hover:border-primary hover:text-primary"
                  >
                    <div className="flex size-12 items-center justify-center rounded-full border-2 border-dashed border-inherit">
                      <Icon name="Plus" className="size-6" strokeWidth={1.5} />
                    </div>
                    <span className="font-label text-[0.75rem] font-bold uppercase tracking-widest">
                      {t("dashboardUi.vault.registerNewItem")}
                    </span>
                  </Link>
                </li>
              ) : null}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
